import {onMounted, ref} from "vue";
import {conversationApi} from "@/container";
import {ConversationMessage} from "@/conversation/interfaces";
import {MetaData} from "@/core/interfaces/api";

/**
 * Infinite scroll like for customer conversation messages.
 */
const useCustomerConversation = (customerId: number) => {
    const messages = ref<ConversationMessage[]>([]);
    const meta = ref<MetaData | null>(null);
    const isLoading = ref(false);
    const isLoadingMore = ref(false);
    const currentPage = ref(1);

    const fetchPage = async (page: number) => {
        return await conversationApi().customerConversation(customerId, {page});
    };

    const load = async () => {
        isLoading.value = true;
        currentPage.value = 1;
        messages.value = [];

        try {
            const response = await fetchPage(1);
            messages.value = response.data.reverse();
            meta.value = response.meta ?? null;
        } finally {
            isLoading.value = false;
        }
    };

    const loadMore = async () => {
        if (!meta.value || currentPage.value >= meta.value.last_page) {
            return;
        }

        isLoadingMore.value = true;
        currentPage.value += 1;

        try {
            const response = await fetchPage(currentPage.value);
            messages.value = [...response.data.reverse(), ...messages.value];
            meta.value = response.meta ?? null;
        } finally {
            isLoadingMore.value = false;
        }
    };

    const hasMore = () => {
        return meta.value !== null && currentPage.value < meta.value.last_page;
    };

    onMounted(() => {
        load();
    });

    return {
        messages,
        meta,
        isLoading,
        isLoadingMore,
        hasMore,
        load,
        loadMore,
    };
};

export default useCustomerConversation;
